// --------------------------------------------------------------
// Created On: 2026-07-27
// Author: Zachary Thomas
//
// Last Modified: 2026-07-31
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React from "react";
import styles from "./DigitalOutput.module.scss";

// Component that lets the user select a digital output sensor's position and labels.
export default function DigitalOutput(props: Props): Component {
  const disableAllInputs = props.disabled || props.isRented;

  return (
    <div className="row align-items-center mx-1">
      <div className="col g-0">
        <div data-test="digital-output-container" className="container mt-2 py-0">
          <div className={`${styles.body} row p-0 m-0`}>
            <div className="col py-2 pe-2">
              <div className="row align-items-center my-2 my-lg-1">
                {/* Dropdown for selecting sensor position. */}
                <div className="col-auto my-2 my-lg-1">
                  <label className={styles.title}>Sensor Position</label>
                  <select
                    data-test="digital-output-number-selection"
                    className="form-select"
                    value={props.position}
                    onChange={(e) =>
                      props.onChange(
                        props.formId,
                        parseInt(e.target.value, 10),
                        props.operationLabel,
                        props.trueLabel,
                        props.falseLabel
                      )
                    }
                    disabled={props.disabled}
                  >
                    <option value="-1" disabled>
                      Select...
                    </option>
                    {props.sensorPositions.map((sensorPosition) => (
                      <option
                        key={sensorPosition}
                        value={sensorPosition}
                        disabled={
                          (props.digitalInputsInUse !== null &&
                            props.digitalInputsInUse !== undefined &&
                            Object.keys(props.digitalInputsInUse).includes(String(sensorPosition))) ||
                          props.localSensorPositionsInUse.includes(sensorPosition) ||
                          disableAllInputs
                        }
                      >
                        #{sensorPosition}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Field for entering the operation label*/}
                <div className="col my-2 my-lg-1">
                  <label className={styles.title}>Operation Label</label>
                  <input
                    data-test="do-operation-label-input"
                    className="form-control mx-auto"
                    type="text"
                    maxLength={50}
                    value={props.operationLabel}
                    disabled={disableAllInputs || props.position === -1}
                    onChange={(e) =>
                      props.onChange(props.formId, props.position, e.target.value, props.trueLabel, props.falseLabel)
                    }
                  />
                </div>

                {/* Field for entering true/closed label*/}
                <div className="col my-2 my-lg-1">
                  <label className={`${styles.title} d-none d-lg-inline`}>Label for sending CLOSED signal</label>
                  <label className={`${styles.title} d-inline d-lg-none`}>CLOSED signal</label>
                  <input
                    data-test="do-true-label-input"
                    className="form-control mx-auto"
                    type="text"
                    maxLength={50}
                    value={props.trueLabel}
                    disabled={disableAllInputs || props.position === -1}
                    onChange={(e) =>
                      props.onChange(
                        props.formId,
                        props.position,
                        props.operationLabel,
                        e.target.value,
                        props.falseLabel
                      )
                    }
                  />
                </div>

                {/* Field for entering false/open label*/}
                <div className="col my-2 my-lg-1">
                  <label className={`${styles.title} d-none d-lg-inline`}>Label for sending OPEN signal</label>
                  <label className={`${styles.title} d-inline d-lg-none`}>OPEN signal</label>
                  <input
                    data-test="do-false-label-input"
                    className="form-control mx-auto"
                    type="text"
                    maxLength={50}
                    value={props.falseLabel}
                    disabled={disableAllInputs || props.position === -1}
                    onChange={(e) =>
                      props.onChange(
                        props.formId,
                        props.position,
                        props.operationLabel,
                        props.trueLabel,
                        e.target.value
                      )
                    }
                  />
                </div>

                <div className="col-auto">
                  <button
                    data-test="do-delete-button"
                    type="button"
                    className={`${styles.button} btn btn-danger float-end`}
                    onClick={() => props.onDelete(props.formId)}
                    disabled={props.disabled}
                  >
                    <i className="fa fa-fw fa-times fa-xs" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface Props {
  formIndex: number;
  formId: number;
  isRented: boolean;
  position: number;
  operationLabel: string;
  trueLabel: string;
  falseLabel: string;
  sensorPositions: number[];
  digitalInputsInUse: SensorInfoMap | null;
  localSensorPositionsInUse: number[];
  disabled: boolean;
  onCreate: () => void;
  onChange: (formId: number, position: number, operationLabel: string, trueLabel: string, falseLabel: string) => void;
  onDelete: (formId: number) => void;
}

interface SensorInfoMap {
  [position: number]: SensorInfo;
}

interface SensorInfo {
  reporting: string;
  assetName: string;
}
